/**
 * Module-level singleton managing the three.quarks BatchedRenderer.
 * All particle systems register with this renderer for batched draw calls.
 *
 * Usage:
 *   init(scene) — called once on mount
 *   update(delta) — called every frame in useFrame
 *   getRenderer() — returns BatchedRenderer for system registration
 *   dispose() — cleanup on unmount
 */

import type { Scene } from 'three';
import { BatchedRenderer } from 'three.quarks';

let batchRenderer: BatchedRenderer | null = null;
let initialized = false;

export const particleManager = {
  init(scene: Scene): void {
    if (initialized) return;
    batchRenderer = new BatchedRenderer();
    scene.add(batchRenderer);
    initialized = true;
  },

  update(delta: number): void {
    if (!batchRenderer) return;
    batchRenderer.update(delta);
  },

  getRenderer(): BatchedRenderer {
    if (!batchRenderer) {
      throw new Error('ParticleManager not initialized. Call init(scene) first.');
    }
    return batchRenderer;
  },

  dispose(): void {
    if (batchRenderer) {
      batchRenderer.parent?.remove(batchRenderer);
      batchRenderer = null;
    }
    initialized = false;
  },

  /** For testing — check if initialized */
  isInitialized(): boolean {
    return initialized;
  },
};
